/* CUSTOM:START contact-form-product-picker */
(() => {
  if (customElements.get('contact-form-product-picker')) return;

  const QUERY = `
    query Search($q: String!) {
      search(query: $q, types: [PRODUCT], first: 6) {
        edges {
          node {
            ... on Product {
              title
              handle
              variants(first: 8) {
                nodes {
                  id
                  title
                  sku
                  availableForSale
                  image { url(transform: {maxWidth: 80}) }
                  price { amount currencyCode }
                }
              }
            }
          }
        }
      }
    }
  `;

  const VARIANTS_QUERY = `
    query Variants($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          title
          sku
          availableForSale
          image { url(transform: {maxWidth: 80}) }
          price { amount currencyCode }
          product { title handle }
        }
      }
    }
  `;

  /**
   * Parses URL prefill params. Supports:
   *   ?variant=12345                       (single, qty defaults to 1)
   *   ?variant=12345&qty=2                 (single with qty)
   *   ?variants=12345,67890                (multi, all qty 1)
   *   ?variants=12345:2,67890:1            (multi with qty)
   *   Accepts full gids too: gid://shopify/ProductVariant/12345
   * Returns: [{ gid, numericId, qty }, ...]
   */
  function parsePrefillParams() {
    const params = new URLSearchParams(window.location.search);
    const toGid = (raw) => {
      const id = String(raw).trim();
      if (!id) return null;
      if (id.startsWith('gid://')) return id;
      if (/^\d+$/.test(id)) return `gid://shopify/ProductVariant/${id}`;
      return null;
    };

    const items = [];
    const single = params.get('variant');
    if (single) {
      const gid = toGid(single);
      if (gid) items.push({ gid, qty: parseInt(params.get('qty'), 10) || 1 });
    }
    const multi = params.get('variants');
    if (multi) {
      multi.split(',').forEach((pair) => {
        const [idPart, qtyPart] = pair.split(':');
        const gid = toGid(idPart);
        if (gid) items.push({ gid, qty: parseInt(qtyPart, 10) || 1 });
      });
    }
    return items;
  }

  const escapeHTML = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));

  const formatPrice = (amount, currency) => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount); }
    catch (e) { return `${currency} ${amount}`; }
  };

  function debounce(fn, wait) {
    let t;
    return function debounced() {
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  class ContactFormProductPicker extends HTMLElement {
    constructor() {
      super();
      this.rows = [];
      this.cache = new Map();
    }

    connectedCallback() {
      this.maxRows = parseInt(this.dataset.maxRows, 10) || 5;
      this.apiUrl = this.dataset.apiUrl;
      this.placeholder = this.dataset.placeholder || 'Search products...';
      this.currency = this.dataset.currency || 'USD';
      this.required = this.dataset.required === 'true';

      this.rowsContainer = this.querySelector('[data-cfpp-rows]');
      this.addBtn = this.querySelector('[data-cfpp-add]');
      this.readableField = this.querySelector('[data-cfpp-readable]');
      this.idsField = this.querySelector('[data-cfpp-ids]');
      this.form = this.closest('form');

      this.addBtn.addEventListener('click', () => this.addRow());
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }

      const prefill = parsePrefillParams();
      if (prefill.length) {
        this.applyPrefill(prefill);
      } else {
        this.addRow();
      }
    }

    async applyPrefill(items) {
      // Always start with at least one row in case the fetch fails
      this.addRow();
      try {
        const variants = await this.fetchVariantsByGid(items.map((i) => i.gid));
        items.forEach((item, idx) => {
          const variant = variants[idx];
          if (!variant) return;
          // Reuse the first empty row, then add new rows for subsequent items
          let row = this.rows.find((r) => !r.variant);
          if (!row) {
            if (this.rows.length >= this.maxRows) return;
            this.addRow();
            row = this.rows[this.rows.length - 1];
          }
          this.selectVariant(row, variant);
          row.qtyInput.value = String(Math.max(1, item.qty));
        });
        this.syncFields();
      } catch (err) {
        // Silent fail: empty row is already present so user can still search manually
      }
    }

    async fetchVariantsByGid(gids) {
      const res = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ query: VARIANTS_QUERY, variables: { ids: gids } })
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const json = await res.json();
      const nodes = (json.data && json.data.nodes) || [];
      return nodes.map((v) => {
        if (!v || !v.id) return null;
        return {
          id: v.id,
          numericId: v.id.split('/').pop(),
          productTitle: (v.product && v.product.title) || '',
          productHandle: (v.product && v.product.handle) || '',
          variantTitle: v.title,
          sku: v.sku || '',
          available: !!v.availableForSale,
          image: (v.image && v.image.url) || '',
          price: v.price ? formatPrice(v.price.amount, v.price.currencyCode) : ''
        };
      });
    }

    addRow() {
      if (this.rows.length >= this.maxRows) return;

      const row = document.createElement('div');
      row.className = 'cfpp__row';
      row.innerHTML = `
        <div class="cfpp__search-wrap">
          <input type="text" class="input w-full cfpp__search"
                 placeholder="${escapeHTML(this.placeholder)}"
                 autocomplete="off" spellcheck="false" role="combobox" aria-autocomplete="list" aria-expanded="false">
          <ul class="cfpp__suggestions" role="listbox" hidden></ul>
        </div>
        <quantity-input class="cfpp__qty-wrap">
          <div class="qty-input qty-input--combined inline-flex items-stretch">
            <button type="button" class="qty-input__btn btn btn--minus no-js-hidden" name="minus" aria-label="Decrease quantity">
              <span class="visually-hidden">Decrease quantity</span>
            </button>
            <input type="number" class="qty-input__input input cfpp__qty" min="1" value="1" aria-label="Quantity">
            <button type="button" class="qty-input__btn btn btn--plus no-js-hidden" name="plus" aria-label="Increase quantity">
              <span class="visually-hidden">Increase quantity</span>
            </button>
          </div>
        </quantity-input>
        <button type="button" class="cfpp__remove" aria-label="Remove row" title="Remove">&times;</button>
      `;

      const state = {
        node: row,
        searchInput: row.querySelector('.cfpp__search'),
        suggestionsList: row.querySelector('.cfpp__suggestions'),
        qtyInput: row.querySelector('.cfpp__qty'),
        removeBtn: row.querySelector('.cfpp__remove'),
        variant: null,
        controller: null,
        lastResults: []
      };

      state.searchInput.addEventListener('input', debounce(() => this.handleSearch(state), 250));
      state.searchInput.addEventListener('focus', () => {
        if (state.suggestionsList.children.length > 0) {
          state.suggestionsList.hidden = false;
          state.searchInput.setAttribute('aria-expanded', 'true');
        }
      });
      state.searchInput.addEventListener('blur', () => {
        setTimeout(() => {
          state.suggestionsList.hidden = true;
          state.searchInput.setAttribute('aria-expanded', 'false');
        }, 150);
      });
      state.searchInput.addEventListener('keydown', (e) => this.handleKeyNav(e, state));
      state.qtyInput.addEventListener('change', () => this.syncFields());
      state.qtyInput.addEventListener('input', () => this.syncFields());
      state.removeBtn.addEventListener('click', () => this.removeRow(state));

      this.rowsContainer.appendChild(row);
      this.rows.push(state);
      this.updateAddBtnState();
      this.syncFields();
    }

    removeRow(state) {
      if (this.rows.length <= 1) {
        state.searchInput.value = '';
        state.qtyInput.value = '1';
        state.variant = null;
        state.suggestionsList.innerHTML = '';
        state.suggestionsList.hidden = true;
      } else {
        state.node.remove();
        this.rows = this.rows.filter((r) => r !== state);
      }
      this.updateAddBtnState();
      this.syncFields();
    }

    updateAddBtnState() {
      const atMax = this.rows.length >= this.maxRows;
      this.addBtn.disabled = atMax;
      this.addBtn.style.display = atMax ? 'none' : '';
    }

    async handleSearch(state) {
      const q = state.searchInput.value.trim();
      // typing invalidates previous selection
      state.variant = null;
      this.clearError();

      if (q.length < 2) {
        state.suggestionsList.innerHTML = '';
        state.suggestionsList.hidden = true;
        state.searchInput.setAttribute('aria-expanded', 'false');
        this.syncFields();
        return;
      }

      if (state.controller) state.controller.abort();
      state.controller = new AbortController();

      state.suggestionsList.innerHTML = '<li class="cfpp__loading">Searching...</li>';
      state.suggestionsList.hidden = false;
      state.searchInput.setAttribute('aria-expanded', 'true');

      try {
        const variants = await this.search(q, state.controller.signal);
        this.renderSuggestions(state, variants);
      } catch (err) {
        if (err.name === 'AbortError') return;
        state.suggestionsList.innerHTML = '<li class="cfpp__error">Search failed. Please try again.</li>';
        state.suggestionsList.hidden = false;
      }
      this.syncFields();
    }

    async search(q, signal) {
      if (this.cache.has(q)) return this.cache.get(q);
      const res = await fetch(this.apiUrl, {
        method: 'POST',
        signal,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ query: QUERY, variables: { q } })
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const json = await res.json();
      const edges = (json.data && json.data.search && json.data.search.edges) || [];
      const products = edges.map((e) => e.node).filter(Boolean);
      const variants = [];
      for (const p of products) {
        const vs = (p.variants && p.variants.nodes) || [];
        for (const v of vs) {
          const gid = v.id || '';
          variants.push({
            id: gid,
            numericId: gid.split('/').pop(),
            productTitle: p.title,
            productHandle: p.handle,
            variantTitle: v.title,
            sku: v.sku || '',
            available: !!v.availableForSale,
            image: (v.image && v.image.url) || '',
            price: v.price ? formatPrice(v.price.amount, v.price.currencyCode) : ''
          });
        }
      }
      this.cache.set(q, variants);
      return variants;
    }

    renderSuggestions(state, variants) {
      state.lastResults = variants;
      if (!variants.length) {
        state.suggestionsList.innerHTML = '<li class="cfpp__empty">No matches found</li>';
        state.suggestionsList.hidden = false;
        return;
      }
      state.suggestionsList.innerHTML = variants.map((v, i) => {
        const variantPart = v.variantTitle && v.variantTitle !== 'Default Title'
          ? `<span class="cfpp__suggestion-variant">${escapeHTML(v.variantTitle)}</span>` : '';
        const skuPart = v.sku ? 'SKU ' + escapeHTML(v.sku) + ' &middot; ' : '';
        const stockPart = v.available ? '' : ' &middot; Out of stock';
        return `
          <li class="cfpp__suggestion" role="option" data-index="${i}" tabindex="-1">
            ${v.image ? `<img src="${escapeHTML(v.image)}" alt="" loading="lazy">` : ''}
            <span class="cfpp__suggestion-text">
              <span>${escapeHTML(v.productTitle)}</span>
              ${variantPart}
              <span class="cfpp__suggestion-meta">${skuPart}${escapeHTML(v.price)}${stockPart}</span>
            </span>
          </li>
        `;
      }).join('');
      state.suggestionsList.hidden = false;

      state.suggestionsList.querySelectorAll('.cfpp__suggestion').forEach((el) => {
        // mousedown so we fire before the input blur hides the list
        el.addEventListener('mousedown', (e) => {
          e.preventDefault();
          const idx = parseInt(el.dataset.index, 10);
          if (state.lastResults[idx]) this.selectVariant(state, state.lastResults[idx]);
        });
      });
    }

    selectVariant(state, variant) {
      state.variant = variant;
      const label = variant.variantTitle && variant.variantTitle !== 'Default Title'
        ? `${variant.productTitle} – ${variant.variantTitle}`
        : variant.productTitle;
      state.searchInput.value = label;
      state.suggestionsList.hidden = true;
      state.searchInput.setAttribute('aria-expanded', 'false');
      this.syncFields();
    }

    handleKeyNav(e, state) {
      const items = Array.from(state.suggestionsList.querySelectorAll('.cfpp__suggestion'));
      if (!items.length) return;
      const current = state.suggestionsList.querySelector('[aria-selected="true"]');
      let idx = current ? items.indexOf(current) : -1;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        idx = Math.min(idx + 1, items.length - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        idx = Math.max(idx - 1, 0);
      } else if (e.key === 'Enter' && current) {
        e.preventDefault();
        const i = parseInt(current.dataset.index, 10);
        if (state.lastResults[i]) this.selectVariant(state, state.lastResults[i]);
        return;
      } else if (e.key === 'Escape') {
        state.suggestionsList.hidden = true;
        state.searchInput.setAttribute('aria-expanded', 'false');
        return;
      } else {
        return;
      }
      items.forEach((el) => el.removeAttribute('aria-selected'));
      items[idx].setAttribute('aria-selected', 'true');
      items[idx].scrollIntoView({ block: 'nearest' });
    }

    syncFields() {
      const lines = [];
      const ids = [];
      let n = 0;
      this.rows.forEach((r) => {
        if (!r.variant) return;
        n += 1;
        const qty = parseInt(r.qtyInput.value, 10) || 1;
        const v = r.variant;
        const variantPart = v.variantTitle && v.variantTitle !== 'Default Title' ? ` – ${v.variantTitle}` : '';
        const skuPart = v.sku ? ` – SKU ${v.sku}` : '';
        lines.push(`${n}. ${v.productTitle}${variantPart} – Qty ${qty}${skuPart}`);
        ids.push(v.numericId);
      });
      this.readableField.value = lines.join('\n');
      this.idsField.value = ids.join(', ');
    }

    handleSubmit(e) {
      this.syncFields();
      if (!this.required) return;
      const hasVariant = this.rows.some((r) => r.variant);
      if (!hasVariant) {
        e.preventDefault();
        this.showError('Please select at least one product.');
        this.rows[0] && this.rows[0].searchInput.focus();
      }
    }

    showError(msg) {
      let err = this.querySelector('.cfpp__form-error');
      if (!err) {
        err = document.createElement('p');
        err.className = 'cfpp__form-error form__field-error';
        this.appendChild(err);
      }
      err.textContent = msg;
    }

    clearError() {
      const err = this.querySelector('.cfpp__form-error');
      if (err) err.remove();
    }
  }

  customElements.define('contact-form-product-picker', ContactFormProductPicker);
})();
/* CUSTOM:END contact-form-product-picker */
